import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Quotes, LinkSimple, Star, CaretDown, CaretUp, ChartBar,
} from "@phosphor-icons/react";
import { Tip, platformName, evidenceLabel } from "./commerceUi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DECISIONS = [
  { id: "build", label: "Build", dot: "🟢", on: "bg-emerald-600 text-white", off: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" },
  { id: "watch", label: "Watch", dot: "🟡", on: "bg-amber-500 text-white", off: "bg-amber-50 text-amber-700 hover:bg-amber-100" },
  { id: "reject", label: "Reject", dot: "🔴", on: "bg-rose-600 text-white", off: "bg-rose-50 text-rose-700 hover:bg-rose-100" },
];
const DECISION_BADGE = {
  build: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  watch: "bg-amber-50 text-amber-700 ring-amber-200",
  reject: "bg-rose-50 text-rose-700 ring-rose-200",
};

const scoreTone = (v) => (v == null ? "text-zinc-300" : v >= 70 ? "text-emerald-600" : v >= 40 ? "text-amber-600" : "text-rose-500");

const Metric = ({ label, value, tip, big }) => (
  <div className="text-center">
    <div className={`${big ? "text-2xl" : "text-lg"} font-black leading-none ${scoreTone(value)}`}>{value ?? "—"}</div>
    <div className="text-[9px] mt-0.5 text-zinc-400 font-semibold uppercase tracking-wide flex items-center justify-center">
      {label}{tip && <Tip text={tip} />}
    </div>
  </div>
);

const PillarBar = ({ label, value }) => (
  <div className="flex items-center gap-2">
    <span className="text-[10px] w-24 text-zinc-500 flex-shrink-0">{label}</span>
    <div className="flex-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
      <div className={`h-full rounded-full ${value == null ? "" : value >= 70 ? "bg-emerald-500" : value >= 40 ? "bg-amber-500" : "bg-rose-400"}`}
        style={{ width: `${value || 0}%` }} />
    </div>
    <span className="text-[10px] w-8 text-right font-bold text-zinc-700">{value ?? "—"}</span>
  </div>
);

// ---- Full detail (everything in one place) --------------------------------
const Detail = ({ d }) => {
  const cv = d.layers?.customer_voice || {};
  const si = d.layers?.search_intent || {};
  const bi = d.layers?.buying_intent || {};
  const a = d.ai_assessment || {};
  const esb = d.evidence_strength_breakdown || {};
  return (
    <div className="mt-4 border-t border-zinc-100 pt-4 space-y-4" data-testid={`ws-detail-${d.cluster_id}`}>
      {/* Pillars */}
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5">
        <PillarBar label="Customer Voice" value={d.pillar_scores?.customer_voice} />
        <PillarBar label="Search Intent" value={d.pillar_scores?.search_intent} />
        <PillarBar label="Buying Intent" value={d.pillar_scores?.buying_intent} />
        <PillarBar label="Market Intel" value={d.pillar_scores?.market_intelligence} />
      </div>

      {/* Evidence strength breakdown */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-500">
        <span className="font-bold text-zinc-700">Evidence Strength {d.evidence_strength}</span>
        {Object.entries(esb).map(([k, v]) => (
          <span key={k}>{k.replace(/_/g, " ")}: <b className="text-zinc-700">{Math.round(v * 100)}%</b></span>
        ))}
      </div>

      {/* Voice platforms + keywords */}
      <div className="flex flex-wrap gap-1.5">
        {(d.voice_platforms || []).map((p) => (
          <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 font-semibold">{p}</span>
        ))}
        {(si.keywords_used || []).slice(0, 6).map((k) => (
          <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">{k}</span>
        ))}
      </div>

      {/* Buying intent sub-signals */}
      {bi.sub_signals && Object.keys(bi.sub_signals).length > 0 && (
        <div className="text-[11px] text-zinc-600">
          <span className="font-semibold text-zinc-700">Money signals: </span>
          {Object.entries(bi.sub_signals).map(([k, v]) => (
            <span key={k} className="mr-3">{k.replace(/_/g, " ")}: <b>{v.signal || v.score}</b></span>
          ))}
        </div>
      )}

      {/* Representative quotes */}
      {cv.sample_quotes?.length > 0 && (
        <div className="space-y-1.5">
          {cv.sample_quotes.slice(0, 3).map((q, i) => (
            <div key={i} className="text-xs text-zinc-600 italic flex gap-1.5">
              <Quotes size={13} className="text-violet-300 flex-shrink-0 mt-0.5" weight="fill" />
              <span>"{q.text}" <span className="not-italic text-zinc-400">— {platformName(q.source)}</span></span>
            </div>
          ))}
        </div>
      )}

      {/* Evidence links */}
      <div className="flex flex-wrap gap-2">
        {(cv.evidence || []).slice(0, 5).map((e, i) => (
          <a key={i} href={e.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-violet-600 hover:underline">
            <LinkSimple size={11} /> {platformName(e.platform) || evidenceLabel(e.url)}
          </a>
        ))}
      </div>

      {/* AI assessment */}
      {a.commercial_viability_score != null && (
        <div className="rounded-xl bg-violet-50/60 border border-violet-100 p-3 text-[11px]">
          <div className="flex items-center gap-2 mb-1 text-violet-700 font-bold uppercase tracking-wide text-[10px]">
            <Star size={12} weight="fill" /> AI Business Assessment — advisory
            <span className="ml-auto text-zinc-900 font-black">{a.commercial_viability_score}/100 · {a.ai_recommendation}</span>
          </div>
          {a.ai_reasoning?.slice(0, 3).map((b, i) => (
            <div key={i} className="text-zinc-600 flex gap-1"><span className="text-violet-400">→</span>{b}</div>
          ))}
        </div>
      )}

      {/* Decision history */}
      {d.decision_history?.length > 0 && (
        <div className="text-[11px] text-zinc-500">
          <span className="font-semibold text-zinc-700">Decision history:</span>
          {d.decision_history.map((h, i) => (
            <span key={i} className="ml-2">{DECISIONS.find((x) => x.id === h.decision)?.dot} {h.decision}{h.reason ? ` (${h.reason})` : ""} · {new Date(h.decided_at).toLocaleDateString()}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const Card = ({ card, onDecide, busy }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState(false);
  const [reason, setReason] = useState("");
  const [stars, setStars] = useState(0);

  const toggle = async () => {
    if (!open && !detail) {
      try {
        const { data } = await axios.get(`${API}/commerce/review/workspace/${card.cluster_id}`);
        setDetail(data);
        setDetailErr(false);
      } catch (e) { setDetailErr(true); }
      try {
        const { data } = await axios.get(`${API}/commerce/review/founder-excitement/${card.cluster_id}`);
        if (data.stars) setStars(data.stars);
      } catch (e) { /* noop */ }
    }
    setOpen((v) => !v);
  };

  const rate = async (n) => {
    setStars(n);
    try { await axios.post(`${API}/commerce/review/founder-excitement`, { cluster_id: card.cluster_id, stars: n }); } catch (e) { /* noop */ }
  };

  return (
    <div className="rounded-2xl bg-white/85 backdrop-blur border border-violet-100 p-5 shadow-sm" data-testid={`ws-card-${card.cluster_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wide text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">{card.market}</span>
            <span className="text-[10px] text-zinc-400">{card.stage}</span>
            {card.latest_decision && (
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${DECISION_BADGE[card.latest_decision]}`} data-testid={`ws-badge-${card.cluster_id}`}>
                {DECISIONS.find((x) => x.id === card.latest_decision)?.dot} {card.latest_decision}
              </span>
            )}
          </div>
          <h3 className="mt-2 text-base font-bold text-zinc-900">{card.label || card.cluster_id}</h3>
          {card.sample_quote && <p className="mt-1 text-xs text-zinc-500 italic line-clamp-1">"{card.sample_quote}"</p>}
        </div>
        <div className="flex gap-3 flex-shrink-0">
          <Metric label="Evidence" value={card.evidence_strength} big tip="How strong & corroborated the customer evidence is (multi-platform, repeated)." />
          <Metric label="Confidence" value={card.decision_confidence} big tip="How confident PrimePillar is this should become a business (evidence + AI + demand + buying intent)." />
        </div>
      </div>

      <button onClick={toggle} className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-violet-600 hover:underline" data-testid={`ws-expand-${card.cluster_id}`}>
        {open ? <CaretUp size={12} /> : <CaretDown size={12} />} {open ? "Hide" : "Show"} full evidence
      </button>

      {open && detail && <Detail d={detail} />}
      {open && !detail && detailErr && (
        <div className="mt-3 text-[11px] text-rose-500" data-testid={`ws-detail-error-${card.cluster_id}`}>
          Couldn't load evidence — please retry.
        </div>
      )}

      {/* Decision actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-3">
        {DECISIONS.map((dec) => (
          <button key={dec.id} disabled={busy}
            onClick={() => onDecide(card.cluster_id, dec.id, reason)}
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full transition-all disabled:opacity-50 ${card.latest_decision === dec.id ? dec.on : dec.off}`}
            data-testid={`ws-decide-${dec.id}-${card.cluster_id}`}>
            {dec.dot} {dec.label}
          </button>
        ))}
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason (optional)"
          className="text-xs px-3 py-2 rounded-full border border-zinc-200 focus:border-violet-300 outline-none flex-1 min-w-[140px]"
          data-testid={`ws-reason-${card.cluster_id}`} />
        <div className="flex items-center gap-1 ml-1" data-testid={`ws-excitement-${card.cluster_id}`} title="Founder Excitement — optional, stored permanently for calibration">
          <span className="text-[10px] text-zinc-400 mr-0.5">Excitement</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => rate(n)} disabled={busy}
              className={`text-sm leading-none ${n <= stars ? "text-amber-400" : "text-zinc-300 hover:text-amber-300"}`}
              data-testid={`ws-star-${n}-${card.cluster_id}`}>★</button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Analytics = ({ a }) => (
  <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-white border border-violet-100 p-4 mb-6" data-testid="ws-analytics">
    <div className="flex items-center gap-2 text-violet-700 font-bold text-xs uppercase tracking-wide mb-3">
      <ChartBar size={14} weight="fill" /> Review Analytics
      <span className="ml-auto text-zinc-500 font-medium normal-case">{a.reviewed}/{a.total_opportunities} reviewed ({a.review_progress_pct}%)</span>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      <Metric label="🟢 Build %" value={a.build_pct} />
      <Metric label="🟡 Watch %" value={a.watch_pct} />
      <Metric label="🔴 Reject %" value={a.reject_pct} />
      <Metric label="Avg ES (Builds)" value={a.avg_evidence_strength_of_builds} />
      <Metric label="Avg Confidence (Builds)" value={a.avg_decision_confidence_of_builds} />
      <Metric label="Avg Confidence (All)" value={a.avg_decision_confidence_all} />
    </div>
    {a.top_markets_by_build_quality?.length > 0 && (
      <div className="mt-3 text-[11px] text-zinc-600">
        <span className="font-semibold text-zinc-700">Top markets by build quality: </span>
        {a.top_markets_by_build_quality.slice(0, 5).map((m) => (
          <span key={m.market} className="mr-3">{m.market} <b>({m.avg_decision_confidence})</b></span>
        ))}
      </div>
    )}
  </div>
);

const ReviewWorkspace = () => {
  const [cards, setCards] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [sort, setSort] = useState("decision_confidence");
  const [undecidedOnly, setUndecidedOnly] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [w, an] = await Promise.all([
        axios.get(`${API}/commerce/review/workspace`, { params: { sort, undecided_only: undecidedOnly, limit: 300 } }),
        axios.get(`${API}/commerce/review/analytics`),
      ]);
      setCards(w.data.items);
      setAnalytics(an.data);
    } catch (e) { setCards([]); }
  }, [sort, undecidedOnly]);

  useEffect(() => { load(); }, [load]);

  const decide = async (cluster_id, decision, reason) => {
    setBusy(true);
    setCards((cs) => cs.map((c) => c.cluster_id === cluster_id ? { ...c, latest_decision: decision } : c));
    try {
      await axios.post(`${API}/commerce/review/decide`, { cluster_id, decision, reason });
      const an = await axios.get(`${API}/commerce/review/analytics`);
      setAnalytics(an.data);
      if (undecidedOnly) setCards((cs) => cs.filter((c) => c.cluster_id !== cluster_id));
    } catch (e) { await load(); }
    setBusy(false);
  };

  if (!cards) return <div className="text-center py-16 text-zinc-400" data-testid="ws-loading">Loading review workspace…</div>;

  const SORTS = [
    { id: "decision_confidence", label: "Decision Confidence" },
    { id: "evidence_strength", label: "Evidence Strength" },
    { id: "overall_score", label: "Opportunity Score" },
  ];

  return (
    <div data-testid="commerce-review-workspace">
      {analytics && <Analytics a={analytics} />}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs text-zinc-500">Sort by:</span>
        {SORTS.map((s) => (
          <button key={s.id} onClick={() => setSort(s.id)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all ${sort === s.id ? "bg-violet-600 text-white shadow-sm" : "bg-white text-zinc-600 border border-zinc-200 hover:border-violet-300"}`}
            data-testid={`ws-sort-${s.id}`}>{s.label}</button>
        ))}
        <button onClick={() => setUndecidedOnly((v) => !v)}
          className={`ml-auto text-xs font-bold px-3 py-1.5 rounded-full transition-all ${undecidedOnly ? "bg-violet-600 text-white shadow-sm" : "bg-white text-zinc-600 border border-zinc-200 hover:border-violet-300"}`}
          data-testid="ws-undecided-toggle">{undecidedOnly ? "Showing undecided" : "Show all"}</button>
      </div>

      <div className="space-y-4" data-testid="ws-list">
        {cards.length === 0
          ? <div className="rounded-2xl border border-dashed border-violet-200 bg-white/60 p-8 text-center text-sm text-zinc-500" data-testid="ws-empty">No opportunities in this view.</div>
          : cards.map((c) => <Card key={c.cluster_id} card={c} onDecide={decide} busy={busy} />)}
      </div>
    </div>
  );
};

export default ReviewWorkspace;
